import { Tabs } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import "assets/stylesheets/components/notificationTabContent.css"

import { colors } from "assets/stylesheets/common"
import { SPACE_PICKER } from "constants/routes"
import {
  dismissAllNotifications,
  getNextNotifications,
  getNotifications,
  updateNotificationStatusToRead,
} from "reduxModules/notification/notifications"
import {
  dismissAllUserNotifications,
  getUserNextNotifications,
  getUserNotifications,
  updateUserNotificationStatusToRead,
} from "reduxModules/notification/user-notifications"
import { endpoints } from "utils/routes"

import NotificationsTabContent from "./NotificationsTabContent"

const NotificationsPopoverContent = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const showSpaceTab =
    location.pathname.substring(1) !== endpoints[SPACE_PICKER]
  const initialActiveKey = showSpaceTab ? "space" : "user"
  const [activeKey, setActiveKey] = useState(initialActiveKey)

  const dispatch = useDispatch()
  const { notifications, paging } = useSelector((state) => state.notifications)
  const userNotifications = useSelector(
    (state) => state.userNotifications.notifications
  )
  const userPaging = useSelector(
    (state) => state.userNotifications.paging
  )

  const spaceContainerRef = useRef(null)
  const userContainerRef = useRef(null)

  const handleScroll = useCallback(
    async (container) => {
      if (!container) return
      if (
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 50
      ) {
        if (!isLoadingMore) {
          setIsLoadingMore(true)
          try {
            if (activeKey === "space" && paging?.next_cursor) {
              await dispatch(getNextNotifications())
            } else if (activeKey === "user" && userPaging?.next_cursor) {
              await dispatch(getUserNextNotifications())
            }
          } finally {
            setTimeout(() => {
              setIsLoadingMore(false)
            }, 500)
          }
        }
      }
    },
    [paging, userPaging, isLoadingMore, activeKey, dispatch]
  )

  useEffect(() => {
    let container = null
    if (activeKey === "space") {
      container = spaceContainerRef.current
    } else if (activeKey === "user") {
      container = userContainerRef.current
    }
    if (!container) return

    const onScroll = () => handleScroll(container)
    container.addEventListener("scroll", onScroll)
    return () => container.removeEventListener("scroll", onScroll)
  }, [activeKey, handleScroll])

  // SPACE NOTIFICATIONS
  const refreshSpaceNotifications = async () => {
    setIsLoading(true)
    await dispatch(getNotifications())
    setIsLoading(false)
  }

  const handleSpaceNotificationRemove = async (id) => {
    setIsLoading(true)
    await dispatch(updateNotificationStatusToRead(id))
    setIsLoading(false)
  }

  const handleSpaceNotificationRemoveAll = async () => {
    setIsLoading(true)
    await dispatch(dismissAllNotifications())
    setIsLoading(false)
  }

  // USER NOTIFICATIONS
  const refreshUserNotifications = async () => {
    setIsLoading(true)
    await dispatch(getUserNotifications())
    setIsLoading(false)
  }

  const handleUserNotificationRemove = async (id) => {
    setIsLoading(true)
    await dispatch(updateUserNotificationStatusToRead(id))
    setIsLoading(false)
  }

  const handleUserNotificationRemoveAll = async () => {
    setIsLoading(true)
    await dispatch(dismissAllUserNotifications())
    setIsLoading(false)
  }

  const onTabChange = async (key) => {
    setActiveKey(key)
    if (key === "space") {
      await refreshSpaceNotifications()
    } else if (key === "user") {
      await refreshUserNotifications()
    }
  }

  const tabs = [
    ...(showSpaceTab
      ? [
        {
          key: "space",
          title: "Space Notifications",
          tabBackgroundColor: colors.white,
          activeBackgroundColor: "#e8f0f3",
          titleBackgroundColor: colors.white,
          textColor: colors.deepBlue,
          children: (
            <NotificationsTabContent
              tabKey="space"
              notifications={notifications}
              refreshNotifications={refreshSpaceNotifications}
              removeNotification={handleSpaceNotificationRemove}
              removeAllNotifications={handleSpaceNotificationRemoveAll}
              isLoading={isLoading}
              isLoadingMore={isLoadingMore}
              containerRef={spaceContainerRef}
            />
          ),
        },
      ]
      : []),
    {
      key: "user",
      title: "User Notification",
      tabBackgroundColor: colors.white,
      activeBackgroundColor: "#e8f0f3",
      titleBackgroundColor: colors.white,
      textColor: colors.deepBlue,
      children: (
        <NotificationsTabContent
          tabKey="user"
          notifications={userNotifications}
          refreshNotifications={refreshUserNotifications}
          removeNotification={handleUserNotificationRemove}
          removeAllNotifications={handleUserNotificationRemoveAll}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          containerRef={userContainerRef}
        />
      ),
    },
  ]

  const getTabLabelStyle = (tab) => ({
    backgroundColor:
      activeKey === tab.key
        ? tab.activeBackgroundColor || "#F0F0F0"
        : tab.tabBackgroundColor || "#FFF",
    borderRadius: "6px",
    padding: "8px",
    position: "relative",
    paddingTop: 12,
  })

  const getTabSpanStyle = (tab) => ({
    color: tab.textColor || "#000",
    fontWeight: activeKey === tab.key ? "600" : "normal",
  })

  return (
    <Tabs
      destroyInactiveTabPane
      className="notifications-tab"
      style={styles.tabs.style}
      tabBarStyle={styles.tabs.tabBarStyle}
      activeKey={activeKey}
      onChange={onTabChange}
      items={tabs.map((tab) => {
        return {
          key: tab.key,
          label: (
            <div style={getTabLabelStyle(tab)}>
              <span style={getTabSpanStyle(tab)}>{tab.title}</span>
              {activeKey === tab.key && (
                <div style={styles.activeTabIndicator} />
              )}
            </div>
          ),
          children: tab.children,
        }
      })}
    />
  )
}

export default NotificationsPopoverContent

const styles = {
  tabs: {
    style: { margin: 0, padding: 0 },
    tabBarStyle: { margin: 0, padding: 0 },
  },
  activeTabIndicator: {
    backgroundColor: colors.deepBlue,
    borderRadius: "4px",
    position: "absolute",
    bottom: "4px",
    left: "4px",
    right: "4px",
    height: "4px",
  },
}
